import { Link } from 'react-router-dom';
import { cn } from '@/lib/cn';

interface LogoProps {
  /** Pixel size — the logo is a circular mark so width = height. */
  size?: number;
  /** Render as a Link to "/" — default true; pass false inside another <a>. */
  asLink?: boolean;
  /** Optional class for the wrapper. */
  className?: string;
}

/**
 * MCCWA brand mark.
 *
 * <p>The transparent PNG at /logo.png is the official circular emblem with the wordmark
 * ("MCCWA" + "MUSLIM COMMUNITY CENTER OF WINCHESTER AREA") already embedded,
 * so we don't render any sibling text. Sizing is fluid via the `size` prop.
 *
 * <p>Accessibility: the image gets an explicit alt; when used as a navigation
 * link, the link itself carries an aria-label so screen readers don't read
 * the alt twice.
 */
export function Logo({ size = 48, asLink = true, className }: LogoProps) {
  const img = (
    <img
      src="/logo.png"
      alt="Muslim Community Center of Winchester Area"
      width={size}
      height={size}
      decoding="async"
      loading="eager"
      className={cn('block h-auto w-auto select-none', className)}
      style={{ width: size, height: size }}
    />
  );

  if (!asLink) return img;

  // Click always scrolls the current page to the top — never navigates
  // away. The Home nav link handles cross-page navigation; the logo is
  // a "back to top" affordance from anywhere on the site. The href stays
  // pointed at "/" so middle-click / Ctrl-click / right-click → "Open
  // link in new tab" still works the way users expect from a brand mark.
  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    // Honor modifier keys + non-primary mouse buttons by letting the
    // browser handle them as a real navigation to "/".
    if (e.defaultPrevented) return;
    if (e.button !== 0) return;
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    e.preventDefault();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <Link
      to="/"
      onClick={handleClick}
      aria-label="Scroll to top of page"
      className="inline-flex items-center"
    >
      {img}
    </Link>
  );
}
